/**
 * Pure non-destructive quantization logic for MIDI notes
 * PPQ = 480 standard
 */

import { MidiNote, QuantizeGrid, QuantizeOptions } from '../types/midi';
import { DEFAULT_PPQ } from './timing';

/**
 * Returns grid interval in ticks for a given grid setting and PPQ
 */
export function getGridTicks(grid: QuantizeGrid, ppq: number = DEFAULT_PPQ): number {
  switch (grid) {
    case '1/4':
      return ppq; // 480
    case '1/8':
      return Math.round(ppq / 2); // 240
    case '1/8T':
      // Eighth triplet: 3 notes in a quarter note (480 / 3 = 160)
      return Math.round(ppq / 3); // 160
    case '1/16':
      return Math.round(ppq / 4); // 120
    case '1/16T':
      // Sixteenth triplet: 3 notes in an eighth note (240 / 3 = 80)
      return Math.round(ppq / 6); // 80
    case '1/32':
      return Math.round(ppq / 8); // 60
    case 'Off':
    default:
      return 1;
  }
}

/**
 * Snap an arbitrary tick position to the nearest grid line (100% snap)
 */
export function snapTickToGrid(
  tick: number,
  grid: QuantizeGrid,
  ppq: number = DEFAULT_PPQ
): number {
  if (grid === 'Off') return Math.max(0, Math.round(tick));
  const gridTicks = getGridTicks(grid, ppq);
  return Math.max(0, Math.round(tick / gridTicks) * gridTicks);
}

/**
 * Apply non-destructive quantization to a single note.
 * Uses rawStartTick and rawDurationTicks if present.
 */
export function quantizeSingleNote(
  note: MidiNote,
  options: QuantizeOptions,
  ppq: number = DEFAULT_PPQ
): MidiNote {
  const rawStart = note.rawStartTick !== undefined ? note.rawStartTick : note.startTick;
  const rawDuration = note.rawDurationTicks !== undefined ? note.rawDurationTicks : note.durationTicks;

  if (note.manuallyEdited || options.grid === 'Off' || options.strength <= 0) {
    return {
      ...note,
      rawStartTick: rawStart,
      rawDurationTicks: rawDuration,
      startTick: rawStart,
      durationTicks: rawDuration,
    };
  }

  const gridTicks = getGridTicks(options.grid, ppq);
  const strength = Math.max(0, Math.min(1, options.strength));

  let finalStart = rawStart;
  if (options.quantizeStart) {
    const targetStart = Math.round(rawStart / gridTicks) * gridTicks;
    const offset = (targetStart - rawStart) * strength;
    finalStart = Math.max(0, Math.round(rawStart + offset));
  }

  let finalDuration = rawDuration;
  if (options.quantizeDuration) {
    // Target duration is rounded to nearest multiple of grid, at least 1 grid unit or half-grid
    const minGridSnap = Math.max(gridTicks, Math.round(rawDuration / gridTicks) * gridTicks);
    const offset = (minGridSnap - rawDuration) * strength;
    finalDuration = Math.max(10, Math.round(rawDuration + offset));
  }

  return {
    ...note,
    rawStartTick: rawStart,
    rawDurationTicks: rawDuration,
    startTick: finalStart,
    durationTicks: finalDuration,
  };
}

/**
 * Quantize a collection of notes non-destructively
 */
export function quantizeNotes(
  notes: MidiNote[],
  options: QuantizeOptions,
  ppq: number = DEFAULT_PPQ
): MidiNote[] {
  return notes.map((note) => quantizeSingleNote(note, options, ppq));
}
